import { Suspense, lazy, ComponentType, useMemo } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { Trans, useTranslation } from 'react-i18next';
import { LeadForm } from './LeadForm';
import { useContent } from '../contexts/ContentContext';
import { LazyVideo } from './LazyVideo';
import { getAssetUrls } from '../utils/media';
import { useMedia } from '../contexts/MediaContext';
import { STATIC_VIDEOS } from '../config/staticMedia';

// Lazy load WOW 3D scene for code splitting
const SceneWOW: ComponentType = lazy(() => import('../SceneWOW'));

export const Hero3D = () => {
  const { t } = useTranslation();
  const { getValue } = useContent();
  const { getFirstByPlacement, getByPlacement } = useMedia();
  const prefersReducedMotion = useReducedMotion();
  const scrollToForm = () => {
    document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToPercorsi = () => {
    document.getElementById('percorsi')?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroTitle = getValue('hero.title', t('hero.title'));
  const heroSubtitle = getValue('hero.subtitle', t('hero.subtitle'));
  const heroBadges = getValue('hero.badges', t('hero.badges', { returnObjects: true }) as string[]);
  const heroFeatures = getValue('hero.features', t('hero.features', { returnObjects: true }) as string[]);
  const heroPills = getValue('hero.pills', t('hero.pills', { returnObjects: true }) as string[]);
  const heroCtaPrimary = getValue('hero.ctaPrimary', t('hero.ctaPrimary'));
  const heroCtaSecondary = getValue('hero.ctaSecondary', t('hero.ctaSecondary'));

  const heroVideoOverline = getValue('hero.media.videoOverline', 'Demo video');
  const heroVideoTitle = getValue(
    'hero.media.videoTitle',
    'Una panoramica immersiva della piattaforma e dei flussi di onboarding.',
  );
  const heroVideoBody = getValue(
    'hero.media.videoBody',
    'In tre minuti scopri come RagAi trasforma il percorso didattico dei tuoi studenti.',
  );
  const heroVideoCta = getValue('hero.media.videoCta', 'Guarda ora');

  const heroImageOverline = getValue('hero.media.imageOverline', 'Showcase visivo');
  const heroImageTitle = getValue(
    'hero.media.imageTitle',
    'Storyboard interattivo dei percorsi educativi RagAi.',
  );
  const heroImageBody = getValue(
    'hero.media.imageBody',
    'Inserisci qui immagini o schermate chiave per mettere in evidenza risultati, UX e branding.',
  );
  const heroImageBadgePrimary = getValue('hero.media.imageBadgePrimary', 'Asset aggiornabili via CMS');
  const heroImageBadgeStatus = getValue('hero.media.imageBadgeStatus', 'Ready');
  const heroImageCardOneLabel = getValue('hero.media.imageCardOneLabel', 'Percorsi');
  const heroImageCardTwoLabel = getValue('hero.media.imageCardTwoLabel', 'Dashboard');

  const heroVideoAsset = getFirstByPlacement('hero.video_demo');
  const heroVisualAssets = getByPlacement('hero.visual_showcase');
  const showcaseLeft = heroVisualAssets[0];
  const showcaseRight = heroVisualAssets[1];
  const showcaseLeftUrls = useMemo(
    () => getAssetUrls(showcaseLeft ?? undefined),
    [showcaseLeft],
  );
  const showcaseRightUrls = useMemo(
    () => getAssetUrls(showcaseRight ?? undefined),
    [showcaseRight],
  );
  const heroVideoUrls = heroVideoAsset 
    ? getAssetUrls(heroVideoAsset)
    : { mediaUrl: STATIC_VIDEOS.hero.video, thumbnailUrl: STATIC_VIDEOS.hero.poster || '' };

  return (
    <section
      id="hero"
      data-section="hero"
      className="relative overflow-hidden min-h-[600px] lg:min-h-[700px] flex items-center bg-[linear-gradient(125deg,var(--color-gradient-start)_0%,var(--color-gradient-middle)_45%,var(--color-gradient-end)_100%)]"
    >
      {!prefersReducedMotion && typeof window !== 'undefined' && window.innerWidth >= 768 && (
        <div className="absolute inset-0 z-0">
          <Suspense fallback={null}>
            <SceneWOW />
          </Suspense>
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-r from-[rgba(12,14,44,0.9)] via-[rgba(16,18,58,0.68)] to-transparent lg:to-transparent z-10" />

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 w-full grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
        <div className="animate-section text-white">
          <div className="mb-4 flex flex-wrap gap-2" role="list">
            {heroBadges.map((badge, idx) => (
              <span
                key={`${badge}-${idx}`}
                className="inline-flex items-center rounded-full border border-white/20 px-3 py-1 text-xs font-medium shadow-sm bg-white/10 backdrop-blur text-white"
                role="listitem"
              >
                {badge}
              </span>
            ))}
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight text-white drop-shadow-lg">
            <Trans
              i18nKey="hero.title"
              defaults={heroTitle}
              components={{ 1: <span className="text-regai-accent" /> }}
            />
          </h1>

          <p className="mt-4 text-lg text-gray-200 drop-shadow">
            <Trans i18nKey="hero.subtitle" defaults={heroSubtitle} />
          </p>

          <ul className="mt-6 space-y-2" role="list">
            {heroFeatures.map((feature, idx) => (
              <li key={`${feature}-${idx}`} className="flex gap-3 items-start">
                <svg
                  className="w-5 h-5 mt-1 flex-none text-regai-accent"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M20 7L9 18L4 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-gray-200">{feature}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button onClick={scrollToForm} className="btn btn-primary shadow-lg">
              <Trans i18nKey="hero.ctaPrimary" defaults={heroCtaPrimary} />
            </button>
            <button
              onClick={scrollToPercorsi}
              className="btn btn-ghost backdrop-blur text-white border-white/20 hover:bg-white/25"
            >
              <Trans i18nKey="hero.ctaSecondary" defaults={heroCtaSecondary} />
            </button>
          </div>

          <div className="mt-6 flex flex-wrap gap-2" role="list">
            {heroPills.map((pill, idx) => (
              <span
                key={`${pill}-${idx}`}
                className="pill backdrop-blur"
                role="listitem"
              >
                {pill}
              </span>
            ))}
          </div>

          <div className={`mt-10 grid gap-4 ${(showcaseLeft || showcaseRight) ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
            {heroVideoUrls.mediaUrl ? (
              <div
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/10 backdrop-blur-md p-6 text-left shadow-lg"
                aria-label={t('hero.media.videoAriaLabel')}
                data-media="video-teaser"
              >
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-regai-accent/80">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
                  <Trans i18nKey="hero.media.videoOverline" defaults={heroVideoOverline} />
                </span>
                <p className="mt-3 text-lg font-semibold text-white">
                  <Trans i18nKey="hero.media.videoTitle" defaults={heroVideoTitle} />
                </p>
                <p className="mt-2 text-sm text-gray-300">
                  <Trans i18nKey="hero.media.videoBody" defaults={heroVideoBody} />
                </p>
                <div className="relative mt-4 aspect-video overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(circle_at_top,var(--color-gradient-middle)_0%,var(--color-primary-700)_55%,rgba(10,12,32,0.9)_100%)]">
                  <LazyVideo
                    src={heroVideoUrls.mediaUrl}
                    poster={heroVideoUrls.thumbnailUrl}
                    className="absolute inset-0 h-full w-full object-cover opacity-70"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-black/10" aria-hidden="true" />
                </div>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-regai-accent">
                  <Trans i18nKey="hero.media.videoCta" defaults={heroVideoCta} />
                </span>
              </div>
            ) : (
              <div
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/10 backdrop-blur-md p-6 text-left shadow-lg"
                aria-label={t('hero.media.previewAriaLabel')}
                data-media="video-teaser"
              >
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-regai-accent/80">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
                  <Trans i18nKey="hero.media.videoOverline" defaults={heroVideoOverline} />
                </span>
                <p className="mt-3 text-lg font-semibold text-white">
                  <Trans i18nKey="hero.media.videoTitle" defaults={heroVideoTitle} />
                </p>
                <p className="mt-2 text-sm text-gray-300">
                  <Trans i18nKey="hero.media.videoBody" defaults={heroVideoBody} />
                </p>
                <div className="relative mt-4 aspect-video overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(circle_at_top,var(--color-gradient-middle)_0%,var(--color-primary-700)_55%,rgba(10,12,32,0.9)_100%)]">
                  <div className="pointer-events-none absolute inset-0 bg-black/10" aria-hidden="true" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-md transition group-hover:scale-110">
                      <span className="ml-1 inline-block h-8 w-8 border-y-[12px] border-l-[18px] border-solid border-y-transparent border-l-white" aria-hidden="true" />
                    </span>
                  </span>
                </div>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-regai-accent">
                  <Trans i18nKey="hero.media.videoCta" defaults={heroVideoCta} />
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12h14m-6-6 6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            )}

            {(showcaseLeft || showcaseRight) && (
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[rgba(12,18,54,0.7)] p-6 shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-regai-accent/80">
                  <Trans i18nKey="hero.media.imageOverline" defaults={heroImageOverline} />
                </span>
                <p className="mt-3 text-lg font-semibold text-white">
                  <Trans i18nKey="hero.media.imageTitle" defaults={heroImageTitle} />
                </p>
                <p className="mt-2 text-sm text-gray-300">
                  <Trans i18nKey="hero.media.imageBody" defaults={heroImageBody} />
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/10">
                    {showcaseLeft ? (
                      <img
                        src={showcaseLeftUrls.thumbnailUrl || showcaseLeftUrls.mediaUrl}
                        alt={showcaseLeft.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full bg-[linear-gradient(140deg,rgba(91,63,246,0.65)_0%,rgba(192,60,247,0.35)_100%)]" />
                    )}
                    <span className="absolute inset-0 bg-white/10 mix-blend-overlay" aria-hidden="true" />
                    <span className="absolute bottom-3 left-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-white/80">
                      <Trans i18nKey="hero.media.imageCardOneLabel" defaults={heroImageCardOneLabel} />
                    </span>
                  </div>
                  <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/10">
                    {showcaseRight ? (
                      <img
                        src={showcaseRightUrls.thumbnailUrl || showcaseRightUrls.mediaUrl}
                        alt={showcaseRight.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full bg-[linear-gradient(160deg,rgba(40,80,245,0.55)_0%,rgba(215,141,249,0.4)_100%)]" />
                    )}
                    <span className="absolute inset-0 bg-white/10 mix-blend-overlay" aria-hidden="true" />
                    <span className="absolute bottom-3 left-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-white/80">
                      <Trans i18nKey="hero.media.imageCardTwoLabel" defaults={heroImageCardTwoLabel} />
                    </span>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between text-xs text-gray-400">
                  <span>
                    <Trans i18nKey="hero.media.imageBadgePrimary" defaults={heroImageBadgePrimary} />
                  </span>
                  <span className="inline-flex items-center gap-1 text-emerald-300">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />
                    <Trans i18nKey="hero.media.imageBadgeStatus" defaults={heroImageBadgeStatus} />
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <LeadForm />
      </div>
    </section>
  );
};
